/* PICKING TWO POINTS AND SOLVING BETWEEN THEM.
 *
 * ═══ WHAT THIS IS NOT ═══
 *
 * It is not the boat's route editor. That one binds vertices to SignalK
 * waypoints, drags them live, re-solves on the drag, offers "MOVE WAYPOINT
 * HERE" when a drag walks off a bind, and writes the result back to the
 * server's resources. All of that needs a SignalK server and none of it is
 * here.
 *
 * This is the small half: arm the tool, tap a start, tap an end, hold the
 * answer. It is what a planning chart needs and it is all of what a planning
 * chart can honestly offer, since there is nowhere to save a route TO.
 *
 * ═══ ADVISORY, AND THE NOTICE IS NOT OPTIONAL ═══
 *
 * The router searches a depth-only cost surface built from CHS NONNA. It knows
 * nothing about traffic separation, kelp, a rock awash at half tide, or the
 * fact that Dodd Narrows runs at five knots. A route that comes out looking
 * beautiful is MORE dangerous than one that looks ugly, because it invites
 * trust. `CHS_NOTICE` travels with the store precisely so no UI has an excuse
 * to drop it.
 */

import { autoroute } from './autoroute.svelte.ts';
import { editor } from './editor.svelte.ts';

export type PlanMode = 'off' | 'start' | 'end';

/** A cruising-yacht default, in metres. The boat uses its own keel offset;
 *  a planning chart has no boat, and the panel says which number it used. */
export const DEFAULT_DRAFT_M = 2.0;

class RoutePlan {
  mode = $state<PlanMode>('off');
  start = $state<{ lat: number; lon: number } | null>(null);
  end = $state<{ lat: number; lon: number } | null>(null);
  draftM = $state(DEFAULT_DRAFT_M);

  /** True while the tool wants the next tap. `<Chart>`'s host checks this
   *  before letting a tap reach the pick sheet — one gesture, one meaning. */
  get armed(): boolean {
    return this.mode !== 'off';
  }

  arm(): void {
    this.mode = 'start';
    this.start = null;
    this.end = null;
    autoroute.clear();
    editor.cancel();
  }

  off(): void {
    this.mode = 'off';
    this.start = null;
    this.end = null;
    autoroute.clear();
    editor.cancel();
  }

  /**
   * A tap while armed.
   *
   * The second tap SOLVES, rather than waiting for a separate button: two taps
   * and an answer is the whole interaction, and a third control to press after
   * choosing both ends is a step that exists only because the code wanted it.
   */
  async tap(p: { lat: number; lon: number }): Promise<void> {
    if (this.mode === 'start') {
      this.start = p;
      this.mode = 'end';
      return;
    }
    if (this.mode !== 'end' || !this.start) return;
    this.end = p;
    this.mode = 'off';
    await autoroute.route(this.start, p, this.draftM);
    /* ═══ THE ANSWER LANDS IN THE EDITOR, NOT IN A READ-ONLY BOX ═══
     *
     * A route you cannot change is one you have to re-solve from scratch to
     * adjust, and the router's answer is a starting point far more often than
     * a final one — it knows depth and nothing else. The boat does exactly
     * this: `runAutoroute` loads a genuine result straight into the editor as
     * a draft. */
    const r = autoroute.result;
    if (r && r.length >= 2) editor.startDraft(r.map((p2) => ({ ...p2 })));
  }
}

export const routePlan = new RoutePlan();
